import React from 'react';
import {
  MapPin,
  CalendarDays,
  Clock3,
  ArrowRight,
  Accessibility,
  Droplets,
  Ticket,
  MessageCircle,
  Store,
  Users
} from 'lucide-react';

const brands = [
  {
    name: 'Quida',
    logo: '/seniormarket-logos/quida.jpg',
    url: 'https://quida.cl'
  },
  {
    name: 'Galenicum Vitae',
    logo: '/seniormarket-logos/galenicum-vitae.jpg',
    url: 'https://www.galenicumchile.com'
  },
  {
    name: 'LisTamente',
    logo: '/seniormarket-logos/listamente.jpg',
    url: 'https://www.listamente.com/'
  },
  {
    name: 'ITiSB',
    logo: '/seniormarket-logos/itisb.jpg',
    url: 'https://itisb.unab.cl'
  },
  {
    name: 'Qué Leo',
    logo: '/seniormarket-logos/que-leo.jpg',
    url: 'https://queleochile.cl'
  },
  {
    name: 'La María',
    logo: '/seniormarket-logos/la-maria.jpg',
    url: 'https://www.facebook.com/LaMariaDisenos/'
  },
  {
    name: 'The Knife',
    logo: '/seniormarket-logos/the-knife.jpg'
  },
  {
    name: 'CO TOTO',
    logo: '/seniormarket-logos/co-toto.jpg'
  },
  {
    name: 'Bilbi',
    logo: '/seniormarket-logos/bilbi.jpg',
    url: 'https://bilbi.cl/'
  },
  {
    name: 'Arte Helados',
    logo: '/seniormarket-logos/arte-helados.jpg'
  },
  {
    name: 'LIV UP',
    logo: '/seniormarket-logos/liv-up.jpg',
    url: 'https://livup.cl/'
  },
  {
    name: 'Las Rosas',
    logo: '/seniormarket-logos/las-rosas.jpg'
  },
  {
    name: 'Diosa Luna',
    logo: '/seniormarket-logos/diosaluna.jpg',
    url: 'https://www.instagram.com/diosa_.luna'
  },
  {
    name: 'La Casa de la Olla',
    logo: '/seniormarket-logos/lacasadelaolla.jpg',
    url: 'https://www.instagram.com/lacasadelaolla1984'
  }
];

const SeniorMarket = () => {
  return (
    <div className="min-h-screen bg-[#f7f3e9] text-[#111111]">

      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="bg-white">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-10 pb-10">
          <div className="overflow-hidden rounded-[26px] md:rounded-[38px] shadow-sm">
            <img
              src="/seniormarket-logos/mercado-urbano-en-familia2.jpg"
              alt="Senior Market - Mercado urbano para disfrutar en familia"
              className="w-full h-auto block"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-3 mt-5">
            <div className="bg-[#dfff00] rounded-2xl p-5 flex items-center gap-4">
              <CalendarDays className="w-7 h-7 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider">
                  Fecha
                </p>
                <p className="text-lg font-black">
                  Sábado 07 noviembre
                </p>
              </div>
            </div>

            <div className="bg-[#08d7ee] rounded-2xl p-5 flex items-center gap-4">
              <Clock3 className="w-7 h-7 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider">
                  Horario
                </p>
                <p className="text-lg font-black">
                  12:00 a 20:00 hrs.
                </p>
              </div>
            </div>

            <div className="bg-[#ff5b83] rounded-2xl p-5 flex items-center gap-4">
              <MapPin className="w-7 h-7 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider">
                  Lugar
                </p>
                <p className="text-lg font-black">
                  Obispo Salas 0381
                </p>
                <p className="text-sm">
                  Providencia · Barrio Vaticano Chico
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          INTRO
      ====================================================== */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            <div className="lg:col-span-7">
              <div className="inline-flex bg-[#08d7ee] px-4 py-2 rounded-full text-xs font-black uppercase tracking-[0.16em] mb-7">
                Cultura · Charlas · Encuentro
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
                Más que un mercado,
                <span className="block mt-2">
                  una experiencia
                </span>
                <span className="inline-block bg-[#ff5b83] px-3 mt-2">
                  para todos.
                </span>
              </h1>
            </div>

            <div className="lg:col-span-5">
              <div className="border-l-4 border-[#dfff00] pl-6 md:pl-8 space-y-6">
                <p className="text-xl md:text-2xl leading-relaxed font-medium">
                  <strong>Senior Market</strong> nace como un punto de encuentro
                  que celebra el bienestar, la cultura y la recreación.
                </p>

                <p className="text-lg text-gray-600 leading-relaxed">
                  Un espacio donde las personas mayores son las protagonistas,
                  y donde toda la familia se entretiene y comparte.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          EXPERIENCIA
      ====================================================== */}
      <section className="bg-[#00e7ff] text-[#33404f] py-20 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid lg:grid-cols-2 gap-14 items-end">
            <div>
              <p className="text-[#33404f] text-sm font-black uppercase tracking-[0.22em]">
                Senior Market
              </p>

              <h2 className="text-[#33404f] text-4xl md:text-6xl font-black leading-[0.98]">
                Un sábado
                <span className="block text-[#dfff00]">
                  diferente.
                </span>
              </h2>
            </div>

            <p className="text-xl text-[#33404f] leading-relaxed max-w-xl">
              Un mercado urbano pensado para recorrer, descubrir,
              conversar, aprender y disfrutar en familia.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-14">
            <div className="border-2 border-[#33404f]/70 rounded-3xl p-7 min-h-[210px] flex flex-col justify-between">
              <Store className="w-9 h-9 text-[#dfff00]" />
              <div>
                <p className="text-2xl font-black">Emprendimientos</p>
                <p className="text-[#33404f] text-base font-medium leading-relaxed mt-2">
                  Marcas y propuestas para descubrir.
                </p>
              </div>
            </div>

            <div className="border-2 border-[#33404f]/70 rounded-3xl p-7 min-h-[210px] flex flex-col justify-between">
              <MessageCircle className="w-9 h-9 text-[#33404f]" />
              <div>
                <p className="text-2xl font-black">Conversación</p>
                <p className="text-[#33404f] text-base font-medium leading-relaxed mt-2">
                  Charlas, ideas y espacios de diálogo.
                </p>
              </div>
            </div>

            <div className="border-2 border-[#33404f]/70 rounded-3xl p-7 min-h-[210px] flex flex-col justify-between">
              <Users className="w-9 h-9 text-[#ff5b83]" />
              <div>
                <p className="text-2xl font-black">Familia</p>
                <p className="text-[#33404f] text-base font-medium leading-relaxed mt-2">
                  Una experiencia para compartir entre generaciones.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* =====================================================
          MARCAS
      ====================================================== */}
      <section className="bg-[#dfff00] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="max-w-4xl mb-14">
            <p className="font-black uppercase tracking-[0.18em] text-sm mb-4">
              Expositores
            </p>

            <h2 className="text-5xl md:text-7xl font-black leading-[0.9] tracking-tight">
              MARCAS QUE YA
              <span className="block bg-[#ff5b83] w-fit px-3 mt-3">
                SON PARTE
              </span>
            </h2>

            <p className="text-lg md:text-xl mt-8 max-w-2xl leading-relaxed">
              Encontrarás marcas y emprendimientos que comparten la idea
              de una vida más activa, creativa y consciente.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {brands.map((brand) => {
              const card = (
                <div
                  className="group bg-white rounded-2xl aspect-square p-5 md:p-6 flex items-center justify-center overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  title={brand.name}
                >
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
              );

              return brand.url ? (
                <a
                  key={brand.name}
                  href={brand.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visitar sitio de ${brand.name}`}
                  className="block"
                >
                  {card}
                </a>
              ) : (
                <div key={brand.name}>
                  {card}
                </div>
              );
            })}
          </div>



        </div>
      </section>

      {/* =====================================================
          DIALOGO Y CULTURA
      ====================================================== */}
      <section className="bg-[#34305e] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid lg:grid-cols-2 gap-14">

            <div>
              <p className="text-[#ff5b83] font-black uppercase tracking-[0.18em] text-sm mb-5">
                Conversaciones
              </p>

              <h2 className="text-5xl md:text-7xl font-black leading-[0.92]">
                <span className="block text-white">
                  Espacio de
                </span>
                <span className="block text-[#dfff00]">
                  Diálogo
                </span>
                <span className="block text-[#dfff00]">
                  & Cultura
                </span>
              </h2>
            </div>

            <div className="space-y-4">

              <div className="space-y-4">

                {/* ALBERTO MAYOL */}
                <div className="group bg-white/10 border border-white/10 rounded-3xl p-3 md:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-5 transition-all duration-300 hover:bg-white/[0.13]">

                  <div className="w-full sm:w-[220px] md:w-[250px] h-[125px] md:h-[140px] rounded-2xl overflow-hidden flex-shrink-0 bg-black/20">
                    <img
                      src="/seniormarket-logos/alberto-mayol.jpg"
                      alt="Alberto Mayol"
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>

                  <div className="py-2 px-1 md:pr-4">
                    <span className="inline-block bg-[#ff5b83] text-black px-3 py-1 text-[11px] font-black mb-3">
                      INVITADO
                    </span>

                    <h3 className="text-xl md:text-2xl font-black leading-tight text-white">
                      Alberto Mayol
                    </h3>

                    <p className="text-white/70 text-sm md:text-base mt-2">
                      Sociólogo & académico
                    </p>
                  </div>

                </div>

                {/* VLADIMIRO MIMICA */}
                <div className="group bg-white/10 border border-white/10 rounded-3xl p-3 md:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-5 transition-all duration-300 hover:bg-white/[0.13]">

                  <div className="w-full sm:w-[220px] md:w-[250px] h-[125px] md:h-[140px] rounded-2xl overflow-hidden flex-shrink-0 bg-black/20">
                    <img
                      src="/seniormarket-logos/vladimir.jpg"
                      alt="Vladimiro Mimica"
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>

                  <div className="py-2 px-1 md:pr-4">
                    <span className="inline-block bg-[#ff5b83] text-black px-3 py-1 text-[11px] font-black mb-3">
                      INVITADO
                    </span>

                    <h3 className="text-xl md:text-2xl font-black leading-tight text-white">
                      Vladimiro Mimica
                    </h3>

                    <p className="text-white/70 text-sm md:text-base mt-2">
                      Relator deportivo
                    </p>
                  </div>

                </div>

                {/* ROLANDO RAMOS */}
                <div className="group bg-white/10 border border-white/10 rounded-3xl p-3 md:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-5 transition-all duration-300 hover:bg-white/[0.13]">

                  <div className="w-full sm:w-[220px] md:w-[250px] h-[125px] md:h-[140px] rounded-2xl overflow-hidden flex-shrink-0 bg-black/20">
                    <img
                      src="/seniormarket-logos/rolando-ramos.jpg"
                      alt="Rolando Ramos"
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>

                  <div className="py-2 px-1 md:pr-4">
                    <span className="inline-block bg-[#ff5b83] text-black px-3 py-1 text-[11px] font-black mb-3">
                      INVITADO
                    </span>

                    <h3 className="text-xl md:text-2xl font-black leading-tight text-white">
                      Rolando Ramos
                    </h3>

                    <p className="text-white/70 text-sm md:text-base mt-2">
                      Locutor radial & comunicador
                    </p>
                  </div>

                </div>

                {/* DOCTOR MARCELO BLACUTT */}
                <div className="group bg-white/10 border border-white/10 rounded-3xl p-3 md:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-5 transition-all duration-300 hover:bg-white/[0.13]">

                  <div className="w-full sm:w-[220px] md:w-[250px] h-[125px] md:h-[140px] rounded-2xl overflow-hidden flex-shrink-0 bg-black/20">
                    <img
                      src="/seniormarket-logos/doctor-marcelo.jpg"
                      alt="Doctor Marcelo Blacutt"
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>

                  <div className="py-2 px-1 md:pr-4">
                    <span className="inline-block bg-[#ff5b83] text-black px-3 py-1 text-[11px] font-black mb-3">
                      INVITADO
                    </span>

                    <h3 className="text-xl md:text-2xl font-black leading-tight text-white">
                      Doctor Marcelo Blacutt
                    </h3>

                    <p className="text-white/70 text-sm md:text-base mt-2">
                      Médico
                    </p>
                  </div>

                </div>





              </div>

              <div className="pt-6">
                <p className="text-xl md:text-2xl font-bold max-w-xl">
                  Sigue atento a nuestras redes para conocer el cronograma de la jornada.
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          INFORMACION
      ====================================================== */}
      <section className="bg-[#f7f3e9] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="mb-14">
            <span className="inline-block bg-[#08d7ee] px-5 py-2 font-black text-sm uppercase tracking-wider">
              Información
            </span>

            <h2 className="text-4xl md:text-6xl font-black mt-5">
              Todo lo que necesitas saber
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">

            <div className="bg-white rounded-3xl p-8 border border-black/5">
              <div className="w-14 h-14 rounded-full bg-[#ff5b83] flex items-center justify-center mb-8">
                <Ticket className="w-7 h-7" />
              </div>

              <p className="text-sm uppercase tracking-wider font-bold opacity-50">
                Entrada
              </p>

              <h3 className="text-2xl font-black mt-2">
                Liberada
              </h3>

              <p className="text-gray-500 mt-3">
                Gratuita para todo público.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-black/5">
              <div className="w-14 h-14 rounded-full bg-[#dfff00] flex items-center justify-center mb-8">
                <Accessibility className="w-7 h-7" />
              </div>

              <p className="text-sm uppercase tracking-wider font-bold opacity-50">
                Accesibilidad
              </p>

              <h3 className="text-2xl font-black mt-2">
                Espacio adaptado
              </h3>

              <p className="text-gray-500 mt-3">
                100% adaptado para personas con movilidad reducida.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-black/5">
              <div className="w-14 h-14 rounded-full bg-[#08d7ee] flex items-center justify-center mb-8">
                <Droplets className="w-7 h-7" />
              </div>

              <p className="text-sm uppercase tracking-wider font-bold opacity-50">
                Hidratación
              </p>

              <h3 className="text-2xl font-black mt-2">
                Durante la jornada
              </h3>

              <p className="text-gray-500 mt-3">
                Puntos de hidratación disponibles durante todo el evento.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          UBICACION / CTA FINAL
      ====================================================== */}
      <section className="bg-[#08d7ee] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="bg-black text-white rounded-[32px] md:rounded-[44px] overflow-hidden">

            <div className="grid lg:grid-cols-2">

              <div className="p-8 md:p-14 lg:p-16">
                <p className="text-[#dfff00] text-sm font-black uppercase tracking-[0.18em] mb-5">
                  Acompáñanos
                </p>

                <h2 className="text-4xl md:text-6xl font-black leading-[0.95]">
                  Ven a disfrutar
                  <span className="block">
                    en familia
                  </span>
                  <span className="block text-[#ff5b83]">
                    de un sábado diferente.
                  </span>
                </h2>
              </div>

              <div className="bg-[#f7f3e9] text-black p-8 md:p-14 lg:p-16 flex flex-col justify-center">

                <div className="flex gap-4 mb-7">
                  <MapPin className="w-7 h-7 shrink-0 mt-1" />
                  <div>
                    <p className="font-black text-2xl">
                      Obispo Salas 0381
                    </p>
                    <p className="text-gray-600">
                      Providencia
                    </p>
                    <p className="text-gray-500 italic">
                      Barrio Vaticano Chico
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 mb-9">
                  <Clock3 className="w-7 h-7 shrink-0 mt-1" />
                  <div>
                    <p className="font-black text-xl">
                      Sábado 07 noviembre
                    </p>
                    <p className="text-gray-600">
                      12:00 a 20:00 hrs.
                    </p>
                  </div>
                </div>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Obispo+Salas+0381+Providencia+Chile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-2 bg-[#dfff00] text-black font-black px-7 py-4 rounded-xl hover:translate-x-1 transition-transform"
                >
                  Cómo llegar
                  <ArrowRight className="w-5 h-5" />
                </a>

              </div>

            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default SeniorMarket;
